import { httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs, orderBy, onSnapshot, Unsubscribe, doc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { functions, db } from "@/lib/firebase";
import { ClientReferral, ClientReferralNote, clientReferralSchema } from "@/lib/schema";
import { getUserByUid } from "@/api/users";

// Client referral creation payload
export interface CreateClientReferralPayload {
  companyName: string;
  pointOfContact: string;
  website?: string;
  email: string;
  phoneNumber?: string;
  referredBy: string; // Firebase Auth UID
  rewardAmount?: number; // defaults to 5000
}

// Create client referral using Firebase callable function
export const createClientReferral = async (data: CreateClientReferralPayload): Promise<string> => {
  // Try with custom timeout and additional options
  const createClientReferralCallable = httpsCallable(functions, 'createClientReferral', {
    timeout: 180000, // 3 minutes timeout for client referral creation
  });
  
  try {
    const payload: any = {
      companyName: data.companyName,
      pointOfContact: data.pointOfContact,
      email: data.email,
      referredBy: data.referredBy,
      rewardAmount: data.rewardAmount || 5000
    };

    // Only include optional fields if they have values
    if (data.website && data.website.trim()) {
      payload.website = data.website;
    }
    if (data.phoneNumber && data.phoneNumber.trim()) {
      payload.phoneNumber = data.phoneNumber;
    }
    
    const result = await createClientReferralCallable(payload);
    
    // Handle different response formats from Firebase function
    const responseData = result.data as { success?: boolean; referralId?: string; data?: any } | string;
    
    // If the response is just a string (referralId), return it directly
    if (typeof responseData === 'string') {
      return responseData;
    }
    
    // Handle Firebase function response structure
    if (responseData && typeof responseData === 'object') {
      if (responseData.success && responseData.referralId) {
        return responseData.referralId;
      }
      
      // Handle error response from function
      if (responseData.success === false) {
        const errorData = (responseData as any).error;
        if (errorData) {
          // Create error with proper code and message
          const error = new Error(errorData.message || 'Client referral creation failed');
          (error as any).code = errorData.code;
          throw error;
        }
        throw new Error((responseData as any).data?.message || 'Client referral creation failed');
      }
    }
    
    throw new Error('Invalid response format from createClientReferral function');
    
  } catch (error: any) {
    // Handle Firebase "aborted" error which might contain the actual error in the response
    if (error.code === 'aborted' || error.code === 'functions/aborted') {
      // Don't log errors for duplicate cases - they're expected user behavior
      // Check if this is a 409 Conflict (duplicate) error
      if (error.message?.includes('409') || error.status === 409 || error.httpErrorCode === 409) {
        const duplicateError = new Error('A client referral for this website already exists.');
        (duplicateError as any).code = 'already-exists';
        throw duplicateError;
      }
      
      // Check customData for Firebase function errors
      if (error.customData) {
        try {
          const customData = error.customData;
          
          // Check if customData has the error structure we expect
          if (customData.success === false && customData.error) {
            const actualError = new Error(customData.error.message || 'Client referral creation failed');
            (actualError as any).code = customData.error.code;
            throw actualError;
          }
        } catch (parseError) {
          console.error('Failed to parse customData:', parseError);
        }
      }
      
      // Try to parse error details if available
      if (error.details) {
        try {
          const details = typeof error.details === 'string' ? JSON.parse(error.details) : error.details;
          
          if (details.error) {
            const actualError = new Error(details.error.message || 'Client referral creation failed');
            (actualError as any).code = details.error.code;
            throw actualError;
          }
          
          // Check if details has the nested structure
          if (details.success === false && details.error) {
            const actualError = new Error(details.error.message || 'Client referral creation failed');
            (actualError as any).code = details.error.code;
            throw actualError;
          }
        } catch (parseError) {
          console.error('Failed to parse aborted error details:', parseError);
        }
      }
      
      // If we can't parse the specific error, but we have a functions/aborted error, 
      // it's likely a 409 conflict that Firebase is not properly exposing
      const duplicateError = new Error('A client referral for this website already exists.');
      (duplicateError as any).code = 'already-exists';
      throw duplicateError;
    }
    
    // Handle Firebase function errors with specific error codes
    if (error.code) {
      switch (error.code) {
        case 'unauthenticated':
          throw new Error('You must be logged in to submit client referrals.');
        case 'permission-denied':
          throw new Error('You do not have permission to submit client referrals.');
        case 'invalid-argument':
          throw new Error('Please check your information and try again.');
        case 'already-exists':
          throw new Error('This company has already been referred by you.');
        case 'resource-exhausted':
          throw new Error('You have reached your daily referral limit. Please try again tomorrow.');
        case 'deadline-exceeded':
          throw new Error('Request timed out. Please check your connection and try again.');
        case 'internal':
          throw new Error('A server error occurred. Please try again later.');
        default:
          throw new Error(error.message || 'Failed to submit client referral. Please try again.');
      }
    }
    
    // Handle network errors
    if (error.name === 'NetworkError' || !navigator.onLine) {
      throw new Error('Please check your internet connection and try again.');
    }
    
    // Re-throw the error if it's already a user-friendly message
    if (error.message && typeof error.message === 'string') {
      throw error;
    }
    
    // Special handling for aborted errors (often 409 conflicts)
    if (error.message === 'aborted') {
      console.log('Treating aborted error as duplicate client referral');
      const duplicateError = new Error('A client referral for this website already exists.');
      (duplicateError as any).code = 'already-exists';
      throw duplicateError;
    }
    
    // Fallback error message
    throw new Error('Failed to submit client referral. Please try again.');
  }
};

// Convert client referral using Firebase callable function
export const onClientConverted = async (clientReferralId: string): Promise<{ success: boolean; message: string }> => {
  const onClientConvertedCallable = httpsCallable(functions, 'onClientConverted', {
    timeout: 180000, // 3 minutes timeout for client conversion processing
  });
  
  try {
    const payload = {
      clientReferralId
    };
    
    const result = await onClientConvertedCallable(payload);
    
    // Handle different response formats from Firebase function
    const responseData = result.data as { success?: boolean; message?: string; data?: { message?: string; clientReferralId?: string } } | string;
    
    // If the response is just a success message string
    if (typeof responseData === 'string') {
      return { success: true, message: responseData };
    }
    
    // If the response is an object
    if (responseData && typeof responseData === 'object') {
      // Handle success response with nested data structure
      if (responseData.success === true) {
        return {
          success: true,
          message: responseData.data?.message || responseData.message || 'Client successfully converted'
        };
      }
      
      // Handle error response from function
      if (responseData.success === false) {
        const errorData = (responseData as any).error;
        if (errorData) {
          const error = new Error(errorData.message || 'Client conversion failed');
          (error as any).code = errorData.code;
          throw error;
        }
        throw new Error((responseData as any).message || responseData.data?.message || 'Client conversion failed');
      }
      
      // Handle direct message response (without success field)
      if ((responseData as any).message && (responseData as any).clientReferralId) {
        return {
          success: true,
          message: (responseData as any).message
        };
      }
    }
    
    console.error('Unhandled response format:', responseData);
    throw new Error('Invalid response format from onClientConverted function');
    
  } catch (error: any) {
    console.error('Error in onClientConverted:', error);
    
    // Handle 404 - function not found
    if (error.code === 'functions/not-found' || error.message?.includes('404')) {
      throw new Error('Firebase function not found. The onClientConverted function may not be deployed yet.');
    }
    
    // Handle Firebase "aborted" error which might contain the actual error in the response
    if (error.code === 'functions/aborted' && error.details?.response?.data) {
      const errorResponse = error.details.response.data;
      throw new Error(errorResponse.error?.message || errorResponse.message || 'Client conversion failed');
    }
    
    // Handle other Firebase callable errors
    if (error.code && error.code.startsWith('functions/')) {
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Authentication required. Please log in and try again.');
      }
      if (error.code === 'functions/permission-denied') {
        throw new Error('Permission denied. You may not have access to perform this action.');
      }
      if (error.code === 'functions/unavailable') {
        throw new Error('Service temporarily unavailable. Please try again later.');
      }
      throw new Error(error.message || 'Client conversion failed');
    }
    
    // Handle network errors
    if (error.message?.includes('Failed to fetch') || error.message?.includes('network')) {
      throw new Error('Network error. Please check your internet connection and try again.');
    }
    
    // Re-throw the error if it's already a proper error
    throw error;
  }
};

// Get client referrals for current user directly from Firestore
export const getClientReferralsFromFirestore = async (userId: string): Promise<ClientReferral[]> => {
  try {
    const clientReferralsRef = collection(db, 'client_referrals');
    const q = query(
      clientReferralsRef,
      where('referredBy', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const clientReferrals: ClientReferral[] = [];
    
    querySnapshot.forEach((doc) => {
      try {
        const data = doc.data();
        const clientReferral = clientReferralSchema.parse({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || data.createdAt?.toDate() || new Date(),
        });
        clientReferrals.push(clientReferral);
      } catch (parseError) {
        // Skip invalid documents
      }
    });
    
    return clientReferrals;
  } catch (error: any) {
    throw new Error('Failed to fetch client referrals');
  }
};

// Alias for easier import
export const getClientReferrals = getClientReferralsFromFirestore;

// Client referral status type
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

// Extended client referral type with UI data for admin management
export interface ClientReferralWithUI extends ClientReferral {
  // Referrer information
  referrerName: string;
  referrerEmail: string;
  
  // UI helpers
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
  statusDisplayName: string;
  priority: 'high' | 'medium' | 'low'; // Based on reward amount or other factors
  
  // Notes are included from ClientReferral base type
}

// Get all client referrals for admin management
export const getAdminClientReferrals = async (): Promise<ClientReferralWithUI[]> => {
  try {
    const clientReferralsRef = collection(db, 'client_referrals');
    const q = query(clientReferralsRef, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    
    // Transform to client referrals with UI data
    const clientReferralsWithUI = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        try {
          const data = doc.data();
          
          // Convert Firestore timestamps to Date objects in notes array
          const convertedNotes = (data.notes || []).map((note: any) => ({
            ...note,
            date: note.date?.toDate ? note.date.toDate() : note.date
          }));
          
          const clientReferral = clientReferralSchema.parse({
            id: doc.id,
            ...data,
            notes: convertedNotes,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || data.createdAt?.toDate() || new Date(),
          });
          
          // Fetch referrer information
          const referrer = await getUserByUid(clientReferral.referredBy);
          
          // Create UI-enhanced client referral
          const clientReferralWithUI: ClientReferralWithUI = {
            ...clientReferral,
            referrerName: referrer ? `${referrer.firstName} ${referrer.lastName}` : 'Unknown Referrer',
            referrerEmail: referrer?.email || 'unknown@email.com',
            formattedCreatedAt: clientReferral.createdAt.toLocaleDateString(),
            formattedUpdatedAt: clientReferral.updatedAt.toLocaleDateString(),
            statusDisplayName: getStatusDisplayName(clientReferral.status),
            priority: getPriority(clientReferral.rewardAmount)
          };
          
          return clientReferralWithUI;
        } catch (parseError) {
          // Log parsing errors for debugging
          console.error('Failed to parse client referral document:', doc.id, parseError);
          return null;
        }
      })
    );
    
    // Filter out null values
    return clientReferralsWithUI.filter((referral): referral is ClientReferralWithUI => referral !== null);
  } catch (error: any) {
    throw new Error('Failed to fetch client referrals for admin');
  }
};

// Subscribe to real-time admin client referrals
export const subscribeToAdminClientReferrals = (
  callback: (clientReferrals: ClientReferralWithUI[]) => void
): Unsubscribe => {
  const clientReferralsRef = collection(db, 'client_referrals');
  const q = query(clientReferralsRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, async (snapshot) => {
    try {
      // Transform to client referrals with UI data
      const clientReferralsWithUI = await Promise.all(
        snapshot.docs.map(async (doc) => {
          try {
            const data = doc.data();
            
            // Convert Firestore timestamps to Date objects in notes array
            const convertedNotes = (data.notes || []).map((note: any) => ({
              ...note,
              date: note.date?.toDate ? note.date.toDate() : note.date
            }));
            
            const clientReferral = clientReferralSchema.parse({
              id: doc.id,
              ...data,
              notes: convertedNotes,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || data.createdAt?.toDate() || new Date(),
            });
            
            // Fetch referrer information
            const referrer = await getUserByUid(clientReferral.referredBy);
            
            // Create UI-enhanced client referral
            const clientReferralWithUI: ClientReferralWithUI = {
              ...clientReferral,
              referrerName: referrer ? `${referrer.firstName} ${referrer.lastName}` : 'Unknown Referrer',
              referrerEmail: referrer?.email || 'unknown@email.com',
              formattedCreatedAt: clientReferral.createdAt.toLocaleDateString(),
              formattedUpdatedAt: clientReferral.updatedAt.toLocaleDateString(),
              statusDisplayName: getStatusDisplayName(clientReferral.status),
              priority: getPriority(clientReferral.rewardAmount)
            };
            
            return clientReferralWithUI;
          } catch (parseError) {
            // Log parsing errors for debugging
            console.error('Failed to parse client referral document:', doc.id, parseError);
            return null;
          }
        })
      );
      
      // Filter out null values and call callback
      const validReferrals = clientReferralsWithUI.filter((referral): referral is ClientReferralWithUI => referral !== null);
      callback(validReferrals);
    } catch (error) {
      // Handle subscription errors silently
    }
  }, (error) => {
    // Handle subscription errors silently
  });
};

// Get client referral status counts for admin dashboard
export const getAdminClientReferralStatusCounts = async () => {
  try {
    const clientReferralsRef = collection(db, 'client_referrals');
    const querySnapshot = await getDocs(clientReferralsRef);
    
    const referrals = querySnapshot.docs.map(d => d.data());
    
    return {
      pending: referrals.filter(r => r.status === 'pending').length,
      contacted: referrals.filter(r => r.status === 'contacted').length,
      meeting_scheduled: referrals.filter(r => r.status === 'meeting_scheduled').length,
      converted: referrals.filter(r => r.status === 'converted').length,
      rejected: referrals.filter(r => r.status === 'rejected').length,
    };
  } catch (error) {
    throw new Error('Failed to fetch client referral status counts');
  }
};

// Subscribe to real-time client referral status counts
export const subscribeToAdminClientReferralStatusCounts = (
  callback: (counts: { pending: number; contacted: number; meeting_scheduled: number; converted: number; rejected: number }) => void
): Unsubscribe => {
  const clientReferralsRef = collection(db, 'client_referrals');
  
  return onSnapshot(clientReferralsRef, (snapshot) => {
    const referrals = snapshot.docs.map(d => d.data());
    
    const counts = {
      pending: referrals.filter(r => r.status === 'pending').length,
      contacted: referrals.filter(r => r.status === 'contacted').length,
      meeting_scheduled: referrals.filter(r => r.status === 'meeting_scheduled').length,
      converted: referrals.filter(r => r.status === 'converted').length,
      rejected: referrals.filter(r => r.status === 'rejected').length,
    };
    
    callback(counts);
  }, (error) => {
    // Handle subscription errors silently
  });
};

// Helper functions
const getStatusDisplayName = (status: ClientReferralStatus): string => {
  switch (status) {
    case 'pending': return 'Pending';
    case 'contacted': return 'Contacted';
    case 'meeting_scheduled': return 'Meeting Scheduled';
    case 'converted': return 'Converted';
    case 'rejected': return 'Rejected';
    default: return 'Unknown';
  }
};

const getPriority = (rewardAmount: number): 'high' | 'medium' | 'low' => {
  if (rewardAmount >= 10000) return 'high';
  if (rewardAmount >= 5000) return 'medium';
  return 'low';
};

// Update client referral status
export const updateClientReferralStatus = async (
  referralId: string, 
  newStatus: ClientReferralStatus,
  updatedBy?: string
): Promise<void> => {
  try {
    const clientReferralRef = doc(db, 'client_referrals', referralId);
    
    await updateDoc(clientReferralRef, {
      status: newStatus,
      updatedAt: serverTimestamp(),
      ...(updatedBy && { updatedBy })
    });
  } catch (error) {
    throw new Error(`Failed to update client referral status: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Migration function to add missing updatedAt fields to existing documents
// Add note to client referral
export const addClientReferralNote = async (
  referralId: string, 
  note: string, 
  userId: string
): Promise<void> => {
  try {
    const clientReferralRef = doc(db, 'client_referrals', referralId);
    
    const newNote: ClientReferralNote = {
      note: note.trim(),
      date: new Date(),
      addedBy: userId
    };

    await updateDoc(clientReferralRef, {
      notes: arrayUnion(newNote),
      updatedAt: serverTimestamp()
    });
  } catch (error: any) {
    console.error('Error adding client referral note:', error);
    throw new Error('Failed to add note to client referral');
  }
};

export const migrateClientReferralTimestamps = async (): Promise<void> => {
  try {
    const clientReferralsRef = collection(db, 'client_referrals');
    const querySnapshot = await getDocs(clientReferralsRef);
    
    const updates: Promise<void>[] = [];
    
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      
      // Check if updatedAt is missing
      if (!data.updatedAt) {
        const clientReferralRef = doc(db, 'client_referrals', docSnap.id);
        const updatePromise = updateDoc(clientReferralRef, {
          updatedAt: data.createdAt || serverTimestamp()
        });
        updates.push(updatePromise);
      }
    });
    
    if (updates.length > 0) {
      await Promise.all(updates);
    }
  } catch (error) {
    throw new Error(`Failed to migrate client referral timestamps: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
